import { useEffect, useRef, useState } from "react"
import EyeIcon from "../../../../../assets/icons/icon-eye.svg"
import { Button, TextArea } from "react-aria-components";
import { HttpStatusCode } from "axios";
import { SensitiveDataApi } from "../../../../../modules/apis/SensitiveDataApi";
import { capitalizeFirstLetter as UppercaseFirstLetter } from "../../../../../helpers/StringHelper";
import IconHide from "../../../../../assets/icons/icon-hide.svg"
import LoadingButton from "../../../LoadingButton";
import { usePermissions } from "../../../../../modules/auth/Permissions/PermissionContext";
import { hasPermission } from "../../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../../modules/auth/Permissions/PermissionStrings";

interface ISidePanelSensitiveInfo {
    descriptor: string
    data: any,
    dataTarget: string
}

export default function SidePanelSensitiveInfo({ descriptor, data, dataTarget }: ISidePanelSensitiveInfo) {
    const [hiddenData, setHiddenData] = useState<any>(undefined);
    const [hiddenDataCached, setHiddenDataCached] = useState<any>(undefined);
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const [sensitiveDataApi] = useState(new SensitiveDataApi());
    const [editing, setEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { permissions } = usePermissions();
    
    const canEdit = hasPermission(permissions!, PermissionTypes.write, PermissionConcerns.Edit_PII_Notes);

    async function getData() {
        const timeout = setTimeout(() => setShowLoader(true), 200);
        const request = {
            id: data.id,
            entityName: UppercaseFirstLetter(data.entityName),
            dataTarget
        }
        const result = await sensitiveDataApi.getNotes(request);
        if (result?.status == HttpStatusCode.Ok) {
            setHiddenData(result.data?.data?.notes);
            setHiddenDataCached(result.data?.data?.notes);
        }
        setShowLoader(false);
        clearTimeout(timeout);
    }

    async function saveData(notes: string) {
        setSubmitting(true);
        const request = {
            id: data.id,
            entityName: UppercaseFirstLetter(data.entityName),
            dataTarget,
            notes
        }
        await sensitiveDataApi.updateNotes(request);
        setSubmitting(false);
    }

    function handleEditCancel() {
        setEditing(false)
        setHiddenData(hiddenDataCached)
    }
    function handleEditSave() {
        setEditing(false)
        saveData(hiddenData)
        setHiddenDataCached(hiddenData)
    }

    useEffect(() => {
        setHiddenData(undefined);
        setEditing(false);
    }, [data])

    return (
        <div className="side-panel__info-box side-panel__info-box--sensitive margin__top--s">
            <div className="display--flex alignItems--center gap--xxs">
                <p className="small margin__bottom--xxxs margin__top--xxxs">{descriptor}</p>
                {(hiddenData !== undefined && hiddenData !== null) &&
                    <Button className={"button button--text-only button--with-icon"} onPress={() => {setHiddenData(undefined);setEditing(false)}}>
                        <img src={IconHide}></img>
                        <p>Hide</p>
                    </Button>}
            </div>
            {showLoader && <div className="loaders loaders__panel margin__top--xxs"></div>}
            {!showLoader && (hiddenData === undefined || hiddenData === null) &&
                <Button className={"side-panel__sensitive--hidden"} data-testid="viewSensitive" onPress={getData}>
                    <div className="button--with-icon">
                        <img src={EyeIcon} />
                        <p className="button-text">Show</p>
                    </div>
                </Button>
            }
            {(hiddenData !== undefined && hiddenData !== null) && 
                <TextArea 
                    value={hiddenData} 
                    onChange={(e) => setHiddenData(e.target.value)} 
                    data-testid="writeSensitive" 
                    onClick={() => canEdit && setEditing(true)} 
                    readOnly={!canEdit}
                    className={`side-panel__text-area ${editing ? "side-panel__text-area--editing" : ""} ${!canEdit ? "side-panel__text-area--readonly" : ""} margin__top--xxs`}
                />
            }
            {canEdit && (editing || submitting) &&
                <div className="side-panel__text-area__buttons">
                    <Button className={"button button--secondary button--large"} isDisabled={submitting} onPress={handleEditCancel}>Cancel</Button>
                    <LoadingButton isDisabled={submitting} isLoading={submitting} 
                        className="button button--primary button--large" onPress={handleEditSave}
                        loadingText="Saving"
                        buttonText="Save">
                    </LoadingButton>
                </div>
            }
        </div>
    )
}  